import { useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Right-side slide-over panel used by every infrastructure card. Keeps the
 * dashboard visible in the background while the admin edits a section.
 *
 * Design goals:
 *  - Never navigate away from the dashboard.
 *  - Support autosave badges, collapsible sub-sections and attachments.
 *  - Escape / backdrop click close.
 */
export function SlideOver({ open, onClose, title, subtitle, right = null, width = "max-w-3xl", testid = "infra", children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return (
    <div
      className={cn(
        "fixed inset-0 z-40 transition-opacity duration-200",
        open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      )}
      data-testid={`slideover-${testid}`}
      aria-hidden={!open}
    >
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={onClose} />
      <aside
        className={cn(
          "absolute right-0 top-0 h-full w-full bg-white shadow-2xl flex flex-col transition-transform duration-300",
          width,
          open ? "translate-x-0" : "translate-x-full"
        )}
        role="dialog"
      >
        <header className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 truncate" data-testid="slideover-title">{title}</h2>
            {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {right}
            <button
              type="button"
              onClick={onClose}
              className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"
              data-testid={`slideover-close-${testid}`}
              aria-label="Close"
            >
              <X className="h-4 w-4 text-slate-500" />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </aside>
    </div>
  );
}

export function SaveStatus({ status }) {
  const map = {
    idle: { label: "Saved", cls: "text-slate-400" },
    saving: { label: "Saving…", cls: "text-blue-600" },
    saved: { label: "Saved ✓", cls: "text-emerald-600" },
    error: { label: "Save failed", cls: "text-rose-600" },
  };
  const s = map[status] || map.idle;
  return <span className={cn("text-xs font-medium", s.cls)} data-testid={`save-status-${status || "idle"}`}>{s.label}</span>;
}

export function Section({ title, description, children, defaultOpen = true }) {
  return (
    <details open={defaultOpen} className="group border-b border-slate-100 last:border-b-0">
      <summary className="px-6 py-4 cursor-pointer list-none flex items-center justify-between hover:bg-slate-50 transition-colors">
        <div>
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          {description && <div className="text-xs text-slate-500 mt-0.5">{description}</div>}
        </div>
        <div className="text-slate-400 text-xs group-open:rotate-180 transition-transform">▾</div>
      </summary>
      <div className="px-6 pb-5 pt-1 space-y-4">{children}</div>
    </details>
  );
}
